import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Input, Label } from "@eduviaos/ui-kit";
import {
  SLUG_RULES,
  boundedNameSchema,
  deriveSlug,
  fieldError,
  slugProblemFor,
  slugSchema,
  type ApiError,
} from "@eduviaos/shared";
import { api } from "@/lib/api";
import { tenantPortalHost } from "@/lib/identifiers";
import { BrandColourPicker } from "@/components/branding/BrandColourPicker";
import {
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  defaultModulesForVertical,
  isAvailable,
  type IndexedCatalog,
  useModuleCatalog,
  type Vertical,
} from "@/lib/modules/catalog";

/**
 * Phase B2.3 — `CreateTenantForm` powers Step 1 of the deferred-invite
 * flow. Submitting creates the Tenant document only; no admin user is
 * created and no invite email is sent. The SA continues to the tenant
 * detail page where they configure modules / forms / branding and then
 * explicitly fires the admin invite via `<SendAdminInviteDialog>`.
 *
 * The previous "all-in-one" `InviteTenantForm` is gone — its admin
 * email/name fields are now part of `<SendAdminInviteDialog>` so the
 * email goes out only after the tenant is fully configured.
 */

export interface CreateTenantInput {
  slug: string;
  name: string;
  type: "school" | "coaching_institute" | "study_centre";
  /** Phase B2 — canonical form-template keys to clone into the tenant. */
  form_template_keys: string[];
  /** Phase B2 — whether the tenant admin can edit forms post-onboarding. */
  forms_editable_by_tenant: boolean;
  /** Phase B2.3 — module catalogue keys enabled for this tenant.
   *  ``null`` = "all modules enabled" (default turn-key behaviour).
   *  Empty array = explicitly disable all modules. */
  enabled_modules: string[] | null;
  /** Phase F2a — brand (primary) colour seeded at creation so the
   *  tenant's sign-in / onboarding host renders on-brand from the first
   *  cold load. ``null`` = no branding seeded (platform theme default). */
  primary_color: string | null;
}

/** One row in `GET /onboarding/form-templates?vertical=...`. */
interface FormTemplateSummary {
  key: string;
  name: string;
  slug: string;
  purpose: string;
  description: string;
  field_count: number;
  requires_auth: boolean;
}

interface FormTemplateListResponse {
  vertical: string;
  items: FormTemplateSummary[];
}

interface Props {
  onSubmit: (input: CreateTenantInput) => void;
  isPending: boolean;
  error: ApiError | null;
}

const TYPE_OPTIONS: Array<{ value: CreateTenantInput["type"]; label: string }> = [
  { value: "school", label: "School" },
  { value: "coaching_institute", label: "Coaching institute" },
  { value: "study_centre", label: "Study centre" },
];

const TENANT_NAME_MAX = 160;
const tenantNameSchema = boundedNameSchema(TENANT_NAME_MAX, "Institution name");
const tenantSlugSchema = slugSchema(SLUG_RULES.tenant);

export function CreateTenantForm({ onSubmit, isPending, error }: Props) {
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [editingAddress, setEditingAddress] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [type, setType] = React.useState<CreateTenantInput["type"]>("school");
  const [primaryColor, setPrimaryColor] = React.useState<string | null>(null);

  // Phase B2 form-template state ────────────────────────────────────
  const [selectedTemplates, setSelectedTemplates] = React.useState<Set<string>>(new Set());
  const [templatesUserTouched, setTemplatesUserTouched] = React.useState(false);
  const [formsEditable, setFormsEditable] = React.useState(true);

  // Phase B2.5c modules state ──────────────────────────────────────
  // The catalog is fetched once from the BE (`GET /super-admin/modules`)
  // and cached for 5 minutes. We pre-select the default-set for the
  // currently-selected vertical the moment the catalog lands and reset
  // the selection on every vertical change UNLESS the SA has already
  // touched the picker (mirrors the form-template auto-select UX).
  const catalogQuery = useModuleCatalog();
  const catalog = catalogQuery.data;
  const [enabledModules, setEnabledModules] = React.useState<Set<string>>(() => new Set());
  const [modulesUserTouched, setModulesUserTouched] = React.useState(false);

  // Auto-pre-select the default modules for the chosen vertical the
  // moment the catalog arrives, OR whenever the SA flips the vertical
  // (unless they've already manually edited the selection).
  React.useEffect(() => {
    if (!catalog) return;
    if (modulesUserTouched) return;
    setEnabledModules(defaultModulesForVertical(catalog, type));
  }, [catalog, type, modulesUserTouched]);

  // Switching vertical resets the touched flag so the new vertical's
  // defaults click in cleanly. Same UX as the templates fieldset.
  React.useEffect(() => {
    setModulesUserTouched(false);
  }, [type]);

  // Fetch templates for the currently-selected vertical. The endpoint is
  // public + cheap; we keep it as a query so the response is cached and
  // the SA can switch vertical back-and-forth without re-fetching.
  const templatesQuery = useQuery<FormTemplateListResponse>({
    queryKey: ["onboarding", "form-templates", type],
    queryFn: () =>
      api.get<FormTemplateListResponse>(
        `/onboarding/form-templates?vertical=${encodeURIComponent(type)}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  // Auto-pre-select all templates whenever the vertical changes — unless
  // the SA has already manually edited the selection. This keeps the
  // happy-path one-click for the common case but doesn't fight an SA
  // who deliberately unchecks a template.
  React.useEffect(() => {
    if (templatesUserTouched) return;
    if (!templatesQuery.data) return;
    setSelectedTemplates(new Set(templatesQuery.data.items.map((t) => t.key)));
  }, [templatesQuery.data, templatesUserTouched]);

  // Whenever vertical changes, reset the touched flag so the new
  // vertical's templates auto-select fresh.
  React.useEffect(() => {
    setTemplatesUserTouched(false);
  }, [type]);

  function toggleTemplate(key: string) {
    setTemplatesUserTouched(true);
    setSelectedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleModule(key: string) {
    setModulesUserTouched(true);
    setEnabledModules((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const effectiveSlug = slugTouched ? slug.trim() : deriveSlug(name, SLUG_RULES.tenant);
  const nameFieldError = fieldError(tenantNameSchema, name);
  // Until the SA edits the address, address problems are reported on the
  // name field (the address is derived from it).
  const derivedAddressError = slugTouched
    ? null
    : slugProblemFor(effectiveSlug, SLUG_RULES.tenant, { noun: "institution" });
  const addressError = slugTouched ? fieldError(tenantSlugSchema, slug) : null;
  const nameError = nameFieldError ?? derivedAddressError;
  const visibleNameError = showErrors ? nameError : null;
  const visibleAddressError = showErrors || slug.length > 0 ? addressError : null;
  const portalHost = tenantPortalHost(
    effectiveSlug || "<address>",
    typeof window === "undefined" ? "" : window.location.hostname,
  );

  const onSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (nameError || addressError) {
      if (addressError) setEditingAddress(true);
      return;
    }
    const trimmedSlug = effectiveSlug;
    // If the SA hasn't manually touched the picker, send `null` so the
    // BE picks the vertical-aware default set itself. Sending `null`
    // is also forward-compatible: any future module added to the
    // vertical's default set auto-enables on this tenant.
    onSubmit({
      slug: trimmedSlug,
      name: name.trim(),
      type,
      form_template_keys: Array.from(selectedTemplates),
      forms_editable_by_tenant: formsEditable,
      enabled_modules: modulesUserTouched ? Array.from(enabledModules) : null,
      primary_color: primaryColor,
    });
  };

  const serverError = error ? extractServerErrorMessage(error) : null;

  return (
    // Form owns its own flex column inside the SheetContent
    // (parent has `flex flex-col p-0`). The body scrolls; the footer
    // stays sticky so "Create tenant" is always reachable on any
    // viewport height. min-h-0 is required so the flex child can
    // actually shrink and produce an internal scrollbar.
    <form onSubmit={onSubmitForm} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-6 pb-4 pt-6">
        <div className="grid gap-1.5">
          <Label htmlFor="tenant-name">Institution name</Label>
          <Input
            id="tenant-name"
            required
            autoFocus
            maxLength={TENANT_NAME_MAX}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setShowErrors(true)}
            placeholder="e.g. Bright Future Academy"
            aria-invalid={visibleNameError ? true : undefined}
            aria-describedby={visibleNameError ? "tenant-name-error" : "tenant-address-preview"}
          />
          {visibleNameError ? (
            <p id="tenant-name-error" className="text-destructive text-xs">
              {visibleNameError}
            </p>
          ) : null}
          {editingAddress ? null : (
            <p
              id="tenant-address-preview"
              className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-xs"
              data-testid="tenant-address-preview"
            >
              <span>
                Portal address: <span className="text-foreground font-mono">{portalHost}</span>
              </span>
              <button
                type="button"
                className="text-primary hover:text-primary/80 focus-visible:ring-ring rounded-sm font-medium underline-offset-4 transition-colors duration-150 ease-out hover:underline focus-visible:outline-none focus-visible:ring-2"
                onClick={() => {
                  setSlug(effectiveSlug);
                  setSlugTouched(true);
                  setEditingAddress(true);
                }}
              >
                Edit address
              </button>
            </p>
          )}
        </div>
        {editingAddress ? (
          <div className="grid gap-1.5">
            <Label htmlFor="tenant-slug">Portal address</Label>
            <Input
              id="tenant-slug"
              required
              autoFocus
              maxLength={SLUG_RULES.tenant.maxLength}
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value.toLowerCase());
                setSlugTouched(true);
              }}
              placeholder="bright-future-academy"
              spellCheck={false}
              autoComplete="off"
              aria-invalid={visibleAddressError ? true : undefined}
              aria-describedby="tenant-slug-help"
            />
            <p
              id="tenant-slug-help"
              className={
                visibleAddressError ? "text-destructive text-xs" : "text-muted-foreground text-xs"
              }
            >
              {visibleAddressError ?? (
                <>
                  Lowercase letters, numbers and single hyphens. The portal opens at{" "}
                  <span className="text-foreground font-mono">{portalHost}</span>
                </>
              )}
            </p>
          </div>
        ) : null}
        <div className="grid gap-1.5">
          <Label htmlFor="tenant-type">Type</Label>
          <select
            id="tenant-type"
            value={type}
            onChange={(e) => setType(e.target.value as CreateTenantInput["type"])}
            className="border-input bg-background focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Phase F2a — brand colour seeded at creation ──────────────── */}
        <fieldset
          className="border-border/60 grid gap-3 rounded-md border p-3"
          data-testid="brand-colour-fieldset"
        >
          <legend className="px-1 text-sm font-medium">Brand colour</legend>
          <p className="text-muted-foreground -mt-1 text-xs">
            Pick the institution's primary colour now and their sign-in and onboarding pages render
            on-brand from the very first visit. Leave it as the default to start neutral — the
            institution admin can refine their logo and colours during onboarding.
          </p>
          <BrandColourPicker
            id="tenant-primary-colour"
            label="Primary colour"
            description="Used for buttons, links and accents across the tenant's portal."
            value={primaryColor}
            onChange={setPrimaryColor}
          />
        </fieldset>

        {/* Phase B2.5c — Modules picker (API-driven, vertical-aware) ── */}
        <fieldset
          className="border-border/60 grid gap-3 rounded-md border p-3"
          data-testid="modules-fieldset"
        >
          <legend className="px-1 text-sm font-medium">Modules</legend>
          <p className="text-muted-foreground -mt-1 text-xs">
            User-facing feature areas the tenant admin will see in their portal. We pre-select the
            default set for{" "}
            {type === "school"
              ? "schools"
              : type === "coaching_institute"
                ? "coaching institutes"
                : "study centres"}
            ; uncheck anything you don't need or check additional modules available for this
            vertical. Cross-vertical modules are hidden here — flip the override toggle on the
            tenant detail page after creating it.
          </p>
          {catalogQuery.isLoading ? (
            <p className="text-muted-foreground py-2 text-xs">Loading module catalog…</p>
          ) : catalogQuery.isError || !catalog ? (
            <p className="text-destructive py-2 text-xs">
              Failed to load module catalog. The tenant will be created with the BE's default set
              for this vertical (you can adjust modules afterwards from the tenant detail page).
            </p>
          ) : (
            <CategoryGroups
              catalog={catalog}
              vertical={type}
              enabled={enabledModules}
              onToggle={toggleModule}
            />
          )}
        </fieldset>

        {/* Phase B2 — Form templates checklist ───────────────────────── */}
        <fieldset
          className="border-border/60 grid gap-2 rounded-md border p-3"
          data-testid="form-templates-fieldset"
        >
          <legend className="px-1 text-sm font-medium">Pre-load form templates</legend>
          <p className="text-muted-foreground -mt-1 text-xs">
            Each vertical ships with canonical forms. They are cloned into the new tenant on
            creation and you can edit them from the tenant's Forms tab before the admin gets the
            invite email. Uncheck any you don't want for this tenant.
          </p>
          {templatesQuery.isLoading ? (
            <p className="text-muted-foreground py-2 text-xs">Loading templates…</p>
          ) : templatesQuery.isError ? (
            <p className="text-destructive py-2 text-xs">
              Failed to load templates. The tenant will be created without pre-loaded forms; you can
              clone them later from the tenant detail page.
            </p>
          ) : (templatesQuery.data?.items.length ?? 0) === 0 ? (
            <p className="text-muted-foreground py-2 text-xs">
              No canonical templates ship for this vertical yet.
            </p>
          ) : (
            <ul className="grid gap-1.5">
              {templatesQuery.data?.items.map((tpl) => {
                const checked = selectedTemplates.has(tpl.key);
                return (
                  <li key={tpl.key} className="flex items-start gap-2">
                    <input
                      id={`template-${tpl.key}`}
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      onChange={() => toggleTemplate(tpl.key)}
                    />
                    <label
                      htmlFor={`template-${tpl.key}`}
                      className="grid cursor-pointer gap-0.5 text-sm"
                    >
                      <span className="font-medium">{tpl.name}</span>
                      <span className="text-muted-foreground text-xs">{tpl.description}</span>
                      <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
                        {tpl.field_count} fields · {tpl.requires_auth ? "internal" : "public"}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </fieldset>

        {/* Phase B2 — Forms-editable-by-tenant toggle ─────────────────── */}
        <div className="border-border/60 flex items-start gap-2 rounded-md border p-3">
          <input
            id="forms-editable"
            type="checkbox"
            className="mt-1"
            checked={formsEditable}
            onChange={(e) => setFormsEditable(e.target.checked)}
          />
          <label htmlFor="forms-editable" className="grid cursor-pointer gap-0.5 text-sm">
            <span className="font-medium">Allow tenant admin to edit forms</span>
            <span className="text-muted-foreground text-xs">
              When ON, the institution admin can create, edit, and delete their tenant's forms via
              the IA portal. When OFF, forms are visible only to you (super-admin / account-manager)
              — the IA can still RENDER forms in admissions and enrollment flows but the Forms
              editor is hidden in their portal. You can flip this any time later from the tenant
              detail page.
            </span>
          </label>
        </div>

        <p className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-xs">
          <strong className="text-foreground font-medium">Next:</strong> after creating the tenant
          you'll land on its detail page where you can fine-tune modules / forms / branding before
          sending the admin invite email.
        </p>

        {serverError && (
          <div
            role="alert"
            className="border-destructive/50 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
          >
            {serverError}
          </div>
        )}
      </div>

      {/* Sticky footer — stays in place while the body scrolls so the
          submit action is always one tap away. */}
      <div className="border-border/60 bg-card flex shrink-0 items-center justify-end gap-2 border-t px-6 py-4">
        <Button type="submit" disabled={isPending}>
          {isPending ? "Creating tenant…" : "Create tenant"}
        </Button>
      </div>
    </form>
  );
}

/**
 * Phase B2.5c — category-grouped module picker for the create-tenant
 * sheet. Renders only modules available for the chosen vertical (no
 * override toggle here — cross-vertical modules require flipping the
 * tenant-level override on the tenant detail page, which is the
 * audited path). Empty categories are skipped so a study-centre's
 * picker doesn't show an empty "Academic" header.
 */
function CategoryGroups({
  catalog,
  vertical,
  enabled,
  onToggle,
}: {
  catalog: IndexedCatalog;
  vertical: Vertical;
  enabled: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="grid gap-3">
      {CATEGORY_ORDER.map((cat) => {
        const visible = catalog.byCategory[cat].filter((m) => isAvailable(m, vertical, false));
        if (visible.length === 0) return null;
        return (
          <div key={cat} className="grid gap-1.5">
            <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
              {CATEGORY_LABEL[cat]} ({visible.length})
            </p>
            <ul className="grid gap-1.5">
              {visible.map((m) => {
                const checked = enabled.has(m.key);
                return (
                  <li key={m.key} className="flex items-start gap-2">
                    <input
                      id={`module-${m.key}`}
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      onChange={() => onToggle(m.key)}
                    />
                    <label
                      htmlFor={`module-${m.key}`}
                      className="grid cursor-pointer gap-0.5 text-sm"
                    >
                      <span className="font-medium">{m.name}</span>
                      <span className="text-muted-foreground text-xs">{m.description}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function extractServerErrorMessage(err: ApiError): string {
  switch (err.code) {
    case "CONFLICT":
      return "Another institution already uses this portal address. Edit the address and retry.";
    case "VALIDATION_ERROR":
      return err.message || "Some fields look off. Check the values and retry.";
    case "FORBIDDEN":
      return "Your account is not authorised to create tenants.";
    case "UNAUTHORIZED":
      return "Your session expired. Sign in again and retry.";
    default:
      return err.message || "Could not create the tenant. Try again in a moment.";
  }
}
